/**
 * ClassWeek — full Klassenbuch view of a single class for a single week.
 *
 * Each cell IS the editor: inline textarea for Inhalt + input for Hausaufgabe,
 * saved on blur via fn_klassenbuch_upsert_diary_entry. No modal, no separate
 * route. Read-only cells (other teachers' lessons) show their content as text.
 *
 * Week paginator above. Host controls the active class + Monday-anchored week.
 */
import { useEffect, useState } from "react";
import { useKioskGetClassWeekQuery } from "../api/klassenbuchKioskApi";
import type { KioskClassWeekCell } from "../types/kioskApiTypes";
import { CourseSquare } from "./CourseSquare";
import type { KioskClassRef } from "../types/chipTypes";
import type { AttendanceStatus, ClassWeekCacheKey } from "../types/kioskApiTypes";
import {
  computeAbsenceRunsByDay,
  DAY_LABELS,
  fmtWeekRange,
  periodForCell,
  PERIOD_ROWS,
} from "../utils/classWeekHelpers";
import { DayNoteCell } from "./DayNoteCell";
import { SummaryRow } from "./SummaryRow";
import { WeekCell } from "./WeekCell";

interface ClassWeekProps {
  activeClass: KioskClassRef;
  weekStart: string; // YYYY-MM-DD Monday
  userId: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  canPrevWeek?: boolean;
  canNextWeek?: boolean;
  /** If set, this lesson is scrolled into view and its attendance panel opened on mount. */
  targetLessonId?: string;
  /** Optional class switcher for the header. When passed with more than
   *  one class, the class name in the header becomes a dropdown. The
   *  kiosk omits this (it has its own class tabs); the in-app
   *  "klassisches Klassenbuch" tab uses it. */
  classes?: KioskClassRef[];
  onClassChange?: (classId: string) => void;
}

export function ClassWeek({
  activeClass,
  weekStart,
  userId,
  onPrevWeek,
  onNextWeek,
  canPrevWeek = true,
  canNextWeek = true,
  targetLessonId,
  classes,
  onClassChange,
}: ClassWeekProps) {
  const { data, isLoading, isFetching, error, refetch } = useKioskGetClassWeekQuery({
    classId: activeClass.id,
    userId,
    weekStart,
  });
  const classWeekKey: ClassWeekCacheKey = { classId: activeClass.id, userId, weekStart };

  // `data` is now `{ cells, calendar_exceptions }`. The queries.ts shim
  // tolerates the legacy pre-migration array shape too, so just access
  // through the new fields.
  const cells = data?.cells ?? [];
  const calendarExceptions = data?.calendar_exceptions ?? [];

  // Build a per-day lookup: dow (1..7) → exception covering that day.
  // First exception wins if there are several on the same day.
  const exceptionByDow: Record<
    number,
    {
      type: string;
      name: string;
      label: string;
    } | undefined
  > = {};
  {
    const weekStartDate = new Date(`${weekStart}T00:00:00`);
    for (let dow = 1; dow <= 7; dow++) {
      const day = new Date(weekStartDate);
      day.setDate(day.getDate() + (dow - 1));
      const p = (n: number) => String(n).padStart(2, "0");
      const iso = `${day.getFullYear()}-${p(day.getMonth() + 1)}-${p(day.getDate())}`;
      const hit = calendarExceptions.find(
        (e) => e.start_date <= iso && iso <= e.end_date,
      );
      if (hit) {
        const isClosed =
          hit.type === "school_closed" ||
          hit.type === "ferientag" ||
          hit.type === "feiertag" ||
          hit.type === "holiday";
        const closedSuffix = isClosed ? " · geschlossen" : "";
        exceptionByDow[dow] = {
          type: hit.type,
          name: hit.name,
          label: `${hit.name || "Feiertag"}${closedSuffix}`,
        };
      }
    }
  }

  const grid: Record<string, KioskClassWeekCell[]> = {};
  const periodTimes: Record<number, { start: string; end: string }> = {};
  const daysWithLessons = new Set<number>();
  for (const c of cells) {
    const dow = Math.min(6, Math.max(1, c.day_of_week));
    const p = periodForCell(c);
    (grid[`${dow}-${p}`] ??= []).push(c);
    daysWithLessons.add(dow);
    // First cell wins for the row label; identical across days for a given period.
    if (!periodTimes[p]) {
      periodTimes[p] = {
        start: new Date(c.start_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
        end:   new Date(c.end_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
      };
    }
  }
  // Show only days that have lessons (drops Saturday for Mo-Fr schools).
  // Default to Mo-Fr if the week has no data at all so the grid still renders.
  const allDays = [1, 2, 3, 4, 5, 6];
  const activeDays =
    daysWithLessons.size > 0
      ? allDays.filter((d) => daysWithLessons.has(d) || !!exceptionByDow[d])
      : [1, 2, 3, 4, 5];

  // Today's ISO weekday (1=Mo … 7=So), and whether the shown week is the
  // current one — used to highlight today's column.
  const todayIsoDow = (() => {
    const d = new Date().getDay();
    return d === 0 ? 7 : d;
  })();
  const isCurrentWeek = (() => {
    const today = new Date();
    const dow = today.getDay() === 0 ? 7 : today.getDay();
    const monday = new Date(today);
    monday.setDate(monday.getDate() - (dow - 1));
    const p = (n: number) => String(n).padStart(2, "0");
    return (
      `${monday.getFullYear()}-${p(monday.getMonth() + 1)}-${p(
        monday.getDate(),
      )}` === weekStart
    );
  })();
  const highlightDow = isCurrentWeek ? todayIsoDow : null;

  // Only one course popover open across the whole grid at a time.
  const [openCourseId, setOpenCourseId] = useState<string | null>(null);

  // Re-tick each minute so the "current lesson" ring tracks the clock.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  // 10-min tolerance — matches WeekCell's JETZT window.
  const isCellCurrent = (c: KioskClassWeekCell) => {
    const s = new Date(c.start_at).getTime();
    const e = new Date(c.end_at).getTime();
    return s - 10 * 60_000 <= nowMs && nowMs < e + 10 * 60_000;
  };

  // Only render period rows that have at least one lesson — drops empty slots
  // (e.g. a 9th period that no class uses) so they don't waste vertical space.
  const periodsWithLessons = new Set(cells.map((c) => periodForCell(c)));
  const runsByDay = computeAbsenceRunsByDay(cells);

  // The rendered period rows, in order.
  const renderedPeriods = PERIOD_ROWS.filter(
    (p) => periodsWithLessons.has(p) || periodsWithLessons.size === 0,
  );

  // Per-DOW date + day note (all cells on the same day share the same day_note).
  const dowInfo: Record<number, { date: string; note: string | null }> = {};
  for (const c of cells) {
    const dow = Math.min(6, Math.max(1, c.day_of_week));
    if (!dowInfo[dow]) {
      dowInfo[dow] = { date: c.start_at.slice(0, 10), note: c.day_note ?? null };
    }
  }

  const formatDayDate = (dow: number) => {
    const fallback = new Date(`${weekStart}T00:00:00`);
    fallback.setDate(fallback.getDate() + (dow - 1));

    const sourceDate = dowInfo[dow]?.date
      ? new Date(`${dowInfo[dow].date}T00:00:00`)
      : fallback;

    return sourceDate.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  useEffect(() => {
    const onAttendanceSaved = () => {
      void refetch();
    };
    window.addEventListener("kiosk-attendance-saved", onAttendanceSaved);
    return () => window.removeEventListener("kiosk-attendance-saved", onAttendanceSaved);
  }, [refetch]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <section className="flex-1 bg-white text-slate-900 rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col font-sans">
        <header className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 gap-3">
          <button
            onClick={onPrevWeek}
            disabled={!canPrevWeek}
            className="px-2.5 py-1 rounded-md bg-white hover:bg-slate-100 text-slate-700 text-sm border border-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
            title="vorige Woche"
          >
            ←
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate flex items-baseline gap-1.5">
              <span>Klassenbuch ·</span>
              {classes && onClassChange && classes.length > 1 ? (
                <select
                  value={activeClass.id}
                  onChange={(e) => onClassChange(e.target.value)}
                  aria-label="Klasse wählen"
                  className="text-lg font-semibold text-slate-900 bg-transparent cursor-pointer rounded-md border border-dashed border-slate-400 px-1.5 py-0.5 hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                >
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.is_klassenleiter ? " ★" : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <span>
                  {activeClass.name}
                  {activeClass.is_klassenleiter ? " ★" : ""}
                </span>
              )}
            </h2>
            <span className="text-sm text-slate-500 whitespace-nowrap font-sans">
              {fmtWeekRange(weekStart)}
            </span>
            {isFetching ? (
              <span className="text-xs text-slate-500">lädt…</span>
            ) : null}
          </div>
          <button
            onClick={onNextWeek}
            disabled={!canNextWeek}
            className="px-2.5 py-1 rounded-md bg-white hover:bg-slate-100 text-slate-700 text-sm border border-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
            title="nächste Woche"
          >
            →
          </button>
        </header>

        {error ? (
          <div className="p-6 text-rose-700 text-sm">
            Konnte Klassenbuch nicht laden:{" "}
            {(error as { message?: string })?.message ?? "Unbekannter Fehler"}
          </div>
        ) : isLoading ? (
          <div className="p-12 text-stone-500 text-sm text-center">lädt…</div>
        ) : (
          <table className="w-full table-fixed border-collapse text-sm flex-1">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-14 px-2 py-2 text-xs uppercase tracking-wider text-slate-500 border-r border-slate-200">
                  Std.
                </th>
                {activeDays.map((dow) => {
                  const exception = exceptionByDow[dow];
                  // Holiday / school-closed days get an amber wash on the
                  // header (and the body cells below) instead of the
                  // today-highlight blue, so the closure is unmistakeable.
                  const headerBg = exception
                    ? "bg-amber-50 text-amber-900"
                    : dow === highlightDow
                    ? "bg-blue-100 text-blue-900"
                    : "text-slate-800";
                  return (
                    <th
                      key={dow}
                      className={`px-2 py-2 text-center text-base font-semibold border-r last:border-r-0 border-slate-200 ${headerBg}`}
                    >
                      <div className="inline-flex items-baseline gap-2">
                        <span>{DAY_LABELS[dow - 1]}</span>
                        <span
                          className={`text-[11px] font-sans ${
                            exception
                              ? "text-amber-700"
                              : dow === highlightDow
                              ? "text-blue-700"
                              : "text-slate-500"
                          }`}
                        >
                          {formatDayDate(dow)}
                        </span>
                      </div>
                      {exception && (
                        <div className="mt-1 text-[11px] font-normal text-amber-800">
                          {exception.label}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {renderedPeriods.map((p) => (
                <tr key={p} className="border-b border-slate-200 last:border-b-0">
                  <td className="px-2 py-1.5 text-center text-slate-500 border-r border-slate-200 align-top">
                    <div className="text-sm">{p}.</div>
                    {periodTimes[p] ? (
                      <div className="text-[9px] text-slate-400 mt-0.5 leading-tight whitespace-nowrap font-sans">
                        {periodTimes[p].start}
                        <br />
                        {periodTimes[p].end}
                      </div>
                    ) : null}
                  </td>
                  {activeDays.map((dow) => {
                    const list = grid[`${dow}-${p}`] ?? [];
                    // Whole-class lessons sit on top; course (Kurs)
                    // lessons render below as small squares side by side.
                    // An adhoc Ausflug lesson sorts to the top of its slot
                    // so the trip leads; the lessons it displaced stay
                    // visible below it, struck through as ENTFÄLLT.
                    const mainCells = list
                      .filter((c) => !c.is_course)
                      .sort(
                        (a, b) =>
                          Number(b.is_adhoc ?? false) -
                          Number(a.is_adhoc ?? false),
                      );
                    // Skip course tiles entirely on holiday days — the
                    // column header already says "geschlossen" so showing
                    // course meetings beneath would just be misleading
                    // (they can't actually run).
                    const courseCells = exceptionByDow[dow]
                      ? []
                      : list.filter((c) => c.is_course);
                    // Ring the whole period cell — not just the lesson
                    // tile — when it holds the current lesson.
                    const hasCurrent = list.some(isCellCurrent);
                    const exception = exceptionByDow[dow];
                    // Holiday / school-closed column: amber wash beats the
                    // blue today-highlight. If the day has no lessons at
                    // all we render a single centred label so the column
                    // doesn't look mysteriously empty.
                    const bgClass = exception
                      ? "bg-amber-50/60"
                      : dow === highlightDow
                      ? "bg-blue-50/40"
                      : "";
                    return (
                      <td
                        key={dow}
                        className={`border-r last:border-r-0 border-slate-200 align-top p-0 ${bgClass} ${
                          hasCurrent
                            ? "relative z-10 ring-2 ring-inset ring-sky-400"
                            : ""
                        }`}
                      >
                        {list.length === 0 ? (
                          exception ? (
                            <div className="px-2 py-3 text-center text-[11px] text-amber-700 italic">
                              {exception.label}
                            </div>
                          ) : null
                        ) : (
                          <div className="flex flex-col">
                            {mainCells.map((c) => (
                              <WeekCell
                                key={c.lesson_id}
                                cell={c}
                                userId={userId}
                                classWeekKey={classWeekKey}
                                targetLessonId={targetLessonId}
                                dayException={
                                  exception
                                    ? { label: exception.label }
                                    : null
                                }
                              />
                            ))}
                            {courseCells.length > 0 ? (
                              <div className="flex flex-wrap gap-1 p-1 border-t border-slate-100 bg-slate-50/50">
                                {courseCells.map((c) => (
                                  <CourseSquare
                                    key={c.lesson_id}
                                    cell={c}
                                    open={openCourseId === c.lesson_id}
                                    onToggle={() =>
                                      setOpenCourseId((cur) =>
                                        cur === c.lesson_id
                                          ? null
                                          : c.lesson_id,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <SummaryRow
                label="Inf."
                status="absent_excused"
                runsByDay={runsByDay}
                activeDays={activeDays}
              />
              <SummaryRow
                label="U."
                status="absent_unexcused"
                runsByDay={runsByDay}
                activeDays={activeDays}
              />
              <SummaryRow
                label="Beurl."
                status={"beurlaubung" as AttendanceStatus /* placeholder until backend supports */}
                runsByDay={runsByDay}
                activeDays={activeDays}
                hideIfEmpty
              />
              <tr className="border-t border-slate-200 bg-slate-50/70">
                <td className="px-2 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500 border-r border-slate-200 align-top">
                  Bem.
                </td>
                {activeDays.map((dow) => (
                  <td
                    key={dow}
                    className={`border-r last:border-r-0 border-slate-200 px-2 py-1.5 align-top ${
                      dow === highlightDow ? "bg-blue-50/40" : ""
                    }`}
                  >
                    {dowInfo[dow] ? (
                      <DayNoteCell
                        classId={activeClass.id}
                        date={dowInfo[dow].date}
                        initialNote={dowInfo[dow].note}
                        classWeekKey={classWeekKey}
                        userId={userId}
                      />
                    ) : null}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
